import React, { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, setDoc,
  query, orderBy, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import "../Styles/AdminGuidance.css";

const TABS = ["Requests", "Blocked Times", "Email Template", "Calendar"];

const DEFAULT_TEMPLATE = `Hi {{name}},

Thank you so much for applying for a free guidance session — I'm really looking forward to speaking with you!

Here are your session details:

📅  Date: {{date}}
🕐  Time: {{time}} (UK time)
💬  Topic: {{topic}}

Meeting link: {{zoomLink}}

Please feel free to come with any specific questions or things you'd like to discuss. There are no silly questions — this is your time.

Looking forward to our conversation!

Warm regards,
Pooja Biswal
Contract Specialist | The PP Studio
https://pp-studio-website.web.app`;

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function fmtDateShort(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function fmtWhen(t) {
  if (!t) return "";
  return `${fmtDate(t.date)} · ${fmtTime(t.time)}`;
}

function timeAgo(ts) {
  if (!ts?.toDate) return "";
  const diff = Date.now() - ts.toDate().getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return ts.toDate().toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function timeKey(t) { return `${t.date}_${t.time}`; }

function subtractMinutes(hhmm, mins) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = Math.max(0, h * 60 + m - mins);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function fmtRangeWhen(t) {
  return `${fmtDate(t.date)} · ${fmtTime(t.startTime)}–${fmtTime(t.endTime)}`;
}

function RequestCard({ req, onConfirm, onDecline }) {
  const [candidate, setCandidate] = useState(null); // { date, time }
  const [rangeDrafts, setRangeDrafts] = useState({}); // idx -> "HH:MM"
  const [customTime, setCustomTime] = useState({ date: "", time: "" });
  const [decliningOpen, setDecliningOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isCandidate = (date, time) => candidate && candidate.date === date && candidate.time === time;
  const pickExact = (t) => setCandidate({ date: t.date, time: t.time });
  const pickRangeTime = (idx, t, value) => {
    setRangeDrafts(prev => ({ ...prev, [idx]: value }));
    if (value) setCandidate({ date: t.date, time: value });
  };
  const useCustomTime = () => {
    if (!customTime.date || !customTime.time) return;
    setCandidate({ date: customTime.date, time: customTime.time });
  };

  const handleConfirm = async () => {
    if (!candidate) return;
    setBusy(true);
    await onConfirm(req, candidate);
    setBusy(false);
  };

  const handleDecline = async () => {
    setBusy(true);
    await onDecline(req, reason.trim());
    setBusy(false);
  };

  return (
    <div className={`ag-req-card status-${req.status}`}>
      <div className="ag-req-top">
        <div className="ag-req-person">
          <div className="ag-req-avatar">{(req.name || "?")[0].toUpperCase()}</div>
          <div className="ag-req-person-info">
            <strong>{req.name}</strong>
            <a href={`mailto:${req.email}`} className="ag-req-email">{req.email}</a>
          </div>
        </div>
        <div className="ag-req-meta">
          <span className={`ag-badge ${req.status}`}>{req.status}</span>
          {req.round > 1 && <span className="ag-badge round">round {req.round}</span>}
          <span className="ag-req-time">{timeAgo(req.createdAt)}</span>
        </div>
      </div>

      <div className="ag-req-body">
        <div className="ag-req-topic">
          <span className="ag-req-label">Topic</span>
          <span>{req.topic}</span>
        </div>
        {req.message && (
          <div className="ag-req-topic">
            <span className="ag-req-label">Message</span>
            <span>{req.message}</span>
          </div>
        )}

        {req.status === "pending" && (
          <div className="ag-req-topic">
            <span className="ag-req-label">They suggested</span>
            <div className="ag-time-options">
              {(req.proposedTimes || []).map((t, idx) => t.type === "range" ? (
                <div className="ag-time-range-option" key={idx}>
                  <span className="ag-range-label">{fmtRangeWhen(t)}</span>
                  <input
                    type="time"
                    min={t.startTime}
                    max={subtractMinutes(t.endTime, 30)}
                    value={rangeDrafts[idx] || ""}
                    onChange={e => pickRangeTime(idx, t, e.target.value)}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  key={timeKey(t)}
                  className={`ag-time-option${isCandidate(t.date, t.time) ? " selected" : ""}`}
                  onClick={() => pickExact(t)}
                >
                  {isCandidate(t.date, t.time) ? "✓ " : ""}{fmtWhen(t)}
                </button>
              ))}
            </div>
          </div>
        )}

        {req.status === "offered" && (
          <div className="ag-req-topic">
            <span className="ag-req-label">Waiting on their reply — offered</span>
            <div className="ag-time-options">
              {(req.offeredTimes || []).map(t => (
                <span className="ag-time-option static" key={timeKey(t)}>{fmtWhen(t)}</span>
              ))}
            </div>
          </div>
        )}

        {req.status === "declined" && (
          <div className="ag-req-topic">
            <span className="ag-req-label">Waiting on new times from them</span>
            {req.declineReason && <span className="ag-decline-note">You said: "{req.declineReason}"</span>}
          </div>
        )}

        {req.status === "confirmed" && req.confirmedTime && (
          <div className="ag-req-slot">
            <span>📅</span>
            <span>{fmtWhen(req.confirmedTime)}</span>
          </div>
        )}
      </div>

      {req.status === "pending" && (
        <div className="ag-req-decide">
          {!decliningOpen ? (
            <>
              <div className="ag-custom-time-row">
                <input type="date" min={new Date().toISOString().slice(0, 10)}
                  value={customTime.date} onChange={e => setCustomTime(c => ({ ...c, date: e.target.value }))} />
                <input type="time" value={customTime.time}
                  onChange={e => setCustomTime(c => ({ ...c, time: e.target.value }))} />
                <button type="button" className="ag-btn ghost small" onClick={useCustomTime}>Use a different time</button>
              </div>
              <div className="ag-req-actions">
                <button type="button" className="ag-btn approve" disabled={!candidate || busy} onClick={handleConfirm}>
                  {busy ? "Confirming…" : candidate ? `✓ Confirm ${fmtWhen(candidate)}` : "✓ Confirm a time"}
                </button>
                <button type="button" className="ag-btn reject" onClick={() => setDecliningOpen(true)}>✕ None of these work</button>
              </div>
            </>
          ) : (
            <div className="ag-decline-box">
              <textarea
                placeholder="Optional — let them know why, so they can suggest better times (e.g. 'I'm away that week')"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                maxLength={300}
              />
              <div className="ag-req-actions">
                <button type="button" className="ag-btn reject" disabled={busy} onClick={handleDecline}>
                  {busy ? "Sending…" : "Confirm decline & ask for new times"}
                </button>
                <button type="button" className="ag-btn ghost small" onClick={() => setDecliningOpen(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminGuidance() {
  const [tab, setTab] = useState("Requests");
  const [requests, setRequests] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [meetingLink, setMeetingLink] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [addingBlock, setAddingBlock] = useState(false);
  const [newBlock, setNewBlock] = useState({ date: "", time: "", reason: "" });

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqSnap, blockSnap, settingsSnap] = await Promise.all([
        getDocs(query(collection(db, "guidance_requests"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "guidance_blocked"), orderBy("date", "asc"))),
        getDocs(collection(db, "guidance_settings")),
      ]);
      setRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setBlocked(blockSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const tmpl = settingsSnap.docs.find(d => d.id === "email_template");
      if (tmpl?.data()?.body) setTemplate(tmpl.data().body);
      if (tmpl?.data()?.meetingLink) setMeetingLink(tmpl.data().meetingLink);
    } catch {
      showToast("Failed to load data");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const addBlock = async () => {
    if (!newBlock.date) { showToast("Pick a date to block"); return; }
    setAddingBlock(true);
    try {
      await addDoc(collection(db, "guidance_blocked"), {
        date: newBlock.date,
        time: newBlock.time || "",
        reason: newBlock.reason.trim(),
        createdAt: serverTimestamp(),
      });
      setNewBlock({ date: "", time: "", reason: "" });
      showToast("Blocked ✓");
      loadData();
    } catch {
      showToast("Failed to block");
    }
    setAddingBlock(false);
  };

  const removeBlock = async (id) => {
    try {
      await deleteDoc(doc(db, "guidance_blocked", id));
      showToast("Removed");
      loadData();
    } catch {
      showToast("Failed to remove");
    }
  };

  const confirmTime = async (req, chosenTime) => {
    try {
      await updateDoc(doc(db, "guidance_requests", req.id), {
        status: "confirmed",
        confirmedTime: chosenTime,
        confirmedAt: serverTimestamp(),
      });
      showToast(`Confirmed — ${req.name} will get the details by email`);
      loadData();
    } catch {
      showToast("Failed to confirm");
    }
  };

  const declineRequest = async (req, reason) => {
    try {
      await updateDoc(doc(db, "guidance_requests", req.id), { status: "declined", declineReason: reason });
      showToast(`${req.name} has been asked for new times`);
      loadData();
    } catch {
      showToast("Failed to decline");
    }
  };

  const saveTemplate = async () => {
    setTemplateSaving(true);
    try {
      await setDoc(doc(db, "guidance_settings", "email_template"), { body: template, meetingLink });
      setTemplateSaved(true);
      showToast("Saved ✓");
      setTimeout(() => setTemplateSaved(false), 2500);
    } catch {
      showToast("Failed to save");
    }
    setTemplateSaving(false);
  };

  const pending = requests.filter(r => r.status === "pending");
  const offered = requests.filter(r => r.status === "offered");
  const declined = requests.filter(r => r.status === "declined");
  const confirmed = requests.filter(r => r.status === "confirmed");
  const today = new Date().toISOString().slice(0, 10);
  const upcomingBlocked = blocked.filter(b => b.date >= today);
  const pastBlocked = blocked.filter(b => b.date < today);

  const upcomingConfirmed = confirmed.filter(r => r.confirmedTime?.date >= today)
    .sort((a, b) => a.confirmedTime.date.localeCompare(b.confirmedTime.date) || a.confirmedTime.time.localeCompare(b.confirmedTime.time));
  const pastConfirmed = confirmed.filter(r => r.confirmedTime?.date < today);

  return (
    <div className="ag-root">
      {toast && <div className="ag-toast">{toast}</div>}

      <div className="ag-header">
        <div className="ag-header-text">
          <h2>Guidance Sessions</h2>
          <p>Free mentorship — review suggested times, block dates you're not free, everything else emails itself</p>
        </div>
        <div className="ag-header-stats">
          {pending.length > 0 && <span className="ag-badge pending">{pending.length} awaiting you</span>}
          {offered.length > 0 && <span className="ag-badge open">{offered.length} awaiting them</span>}
          <span className="ag-badge approved">{confirmed.length} confirmed</span>
        </div>
      </div>

      <div className="ag-tabs">
        {TABS.map(t => (
          <button key={t} type="button" className={`ag-tab${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>
            {t}
            {t === "Requests" && pending.length > 0 && <span className="ag-tab-dot" />}
          </button>
        ))}
      </div>

      {/* ── REQUESTS ── */}
      {tab === "Requests" && (
        <div className="ag-panel">
          {loading && <div className="ag-loading"><span /><span /><span /></div>}

          {!loading && requests.length === 0 && (
            <div className="ag-empty">
              <span className="ag-empty-icon">📭</span>
              <p>No requests yet.</p>
              <small>Share your guidance page to start receiving applications.</small>
            </div>
          )}

          {pending.length > 0 && (
            <div className="ag-group">
              <h3 className="ag-group-title">Awaiting your review</h3>
              {pending.map(req => (
                <RequestCard key={req.id} req={req} onConfirm={confirmTime} onDecline={declineRequest} />
              ))}
            </div>
          )}

          {offered.length > 0 && (
            <div className="ag-group">
              <h3 className="ag-group-title">Awaiting their reply</h3>
              {offered.map(req => <RequestCard key={req.id} req={req} />)}
            </div>
          )}

          {declined.length > 0 && (
            <div className="ag-group">
              <h3 className="ag-group-title muted">Waiting on new times from them</h3>
              {declined.map(req => <RequestCard key={req.id} req={req} />)}
            </div>
          )}

          {confirmed.length > 0 && (
            <div className="ag-group">
              <h3 className="ag-group-title approved">Confirmed</h3>
              {confirmed.map(req => <RequestCard key={req.id} req={req} />)}
            </div>
          )}
        </div>
      )}

      {/* ── BLOCKED TIMES ── */}
      {tab === "Blocked Times" && (
        <div className="ag-panel">
          <div className="ag-add-slot-card">
            <h3>Block a date or time</h3>
            <p>Leave time blank to block the whole day. Shown to visitors so they don't waste a suggestion on it.</p>
            <div className="ag-slot-form">
              <label className="ag-field">
                <span>Date</span>
                <input type="date" value={newBlock.date} min={today}
                  onChange={e => setNewBlock(s => ({ ...s, date: e.target.value }))} />
              </label>
              <label className="ag-field">
                <span>Time <em>(optional)</em></span>
                <input type="time" value={newBlock.time}
                  onChange={e => setNewBlock(s => ({ ...s, time: e.target.value }))} />
              </label>
              <label className="ag-field wide">
                <span>Reason <em>(optional, shown to visitors)</em></span>
                <input type="text" placeholder="e.g. Away that week, fully booked…"
                  value={newBlock.reason} onChange={e => setNewBlock(s => ({ ...s, reason: e.target.value }))} />
              </label>
              <button type="button" className="ag-btn primary" onClick={addBlock} disabled={addingBlock}>
                {addingBlock ? "Adding…" : "+ Block"}
              </button>
            </div>
          </div>

          <div className="ag-slots-section">
            <h3>Upcoming blocks</h3>
            {upcomingBlocked.length === 0
              ? <p className="ag-muted-p">No blocked dates — add one above if you need independence from a request.</p>
              : upcomingBlocked.map(b => (
                <div key={b.id} className="ag-slot-row">
                  <div className="ag-slot-date-pill">
                    <strong>{fmtDateShort(b.date)}</strong>
                    <span>{new Date(b.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" })}</span>
                  </div>
                  <div className="ag-slot-details">
                    <span className="ag-slot-time">{b.time ? fmtTime(b.time) : "All day"}</span>
                    {b.reason && <em className="ag-slot-label">{b.reason}</em>}
                  </div>
                  <div className="ag-slot-right">
                    <button type="button" className="ag-btn ghost small" onClick={() => removeBlock(b.id)}>Remove</button>
                  </div>
                </div>
              ))
            }

            {pastBlocked.length > 0 && (
              <>
                <h3 className="ag-muted" style={{ marginTop: "2rem" }}>Past blocks</h3>
                {pastBlocked.map(b => (
                  <div key={b.id} className="ag-slot-row past">
                    <div className="ag-slot-date-pill past"><strong>{fmtDateShort(b.date)}</strong></div>
                    <div className="ag-slot-details">
                      <span className="ag-slot-time">{b.time ? fmtTime(b.time) : "All day"}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── EMAIL TEMPLATE ── */}
      {tab === "Email Template" && (
        <div className="ag-panel">
          <div className="ag-template-card">
            <div className="ag-template-intro">
              <h3>Confirmation email</h3>
              <p>Sent automatically the instant someone confirms a time — no copy-pasting needed.</p>
            </div>

            <label className="ag-field" style={{ marginBottom: "1rem" }}>
              <span>Meeting link <em>(used everywhere {"{{zoomLink}}"} appears)</em></span>
              <input type="text" placeholder="https://meet.google.com/…"
                value={meetingLink} onChange={e => setMeetingLink(e.target.value)} />
            </label>

            <div className="ag-placeholders">
              <span>Available placeholders:</span>
              <div className="ag-placeholder-chips">
                {["{{name}}", "{{date}}", "{{time}}", "{{topic}}", "{{zoomLink}}"].map(p => (
                  <code key={p} className="ag-chip">{p}</code>
                ))}
              </div>
            </div>

            <textarea
              className="ag-template-body"
              value={template}
              onChange={e => setTemplate(e.target.value)}
              rows={20}
              spellCheck
            />

            <div className="ag-template-footer">
              <span className="ag-template-hint">This is sent the moment a time is confirmed — from either side of the loop.</span>
              <button type="button" className="ag-btn primary" onClick={saveTemplate} disabled={templateSaving}>
                {templateSaved ? "✓ Saved" : templateSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CALENDAR ── */}
      {tab === "Calendar" && (
        <div className="ag-panel">
          {upcomingConfirmed.length === 0 && pastConfirmed.length === 0 && (
            <div className="ag-empty">
              <span className="ag-empty-icon">📅</span>
              <p>No confirmed sessions yet.</p>
              <small>Confirmed requests will appear here.</small>
            </div>
          )}

          {upcomingConfirmed.length > 0 && (
            <>
              <h3 className="ag-group-title">Upcoming</h3>
              <div className="ag-cal-list">
                {upcomingConfirmed.map(ev => (
                  <div key={ev.id} className="ag-cal-event">
                    <div className="ag-cal-date">
                      <strong>{fmtDateShort(ev.confirmedTime.date)}</strong>
                      <span>{new Date(ev.confirmedTime.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" })}</span>
                    </div>
                    <div className="ag-cal-time">{fmtTime(ev.confirmedTime.time)}</div>
                    <div className="ag-cal-info">
                      <strong>{ev.name}</strong>
                      <span>{ev.email}</span>
                      <em>{ev.topic}</em>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {pastConfirmed.length > 0 && (
            <>
              <h3 className="ag-group-title muted" style={{ marginTop: "2rem" }}>Past sessions</h3>
              <div className="ag-cal-list past">
                {pastConfirmed.map(ev => (
                  <div key={ev.id} className="ag-cal-event past">
                    <div className="ag-cal-date past"><strong>{fmtDateShort(ev.confirmedTime.date)}</strong></div>
                    <div className="ag-cal-time">{fmtTime(ev.confirmedTime.time)}</div>
                    <div className="ag-cal-info">
                      <strong>{ev.name}</strong>
                      <em>{ev.topic}</em>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
